import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { BillView, ChargeView, ServiceItemView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PrintHeader,
  PrintLines,
  PrintSheet,
  PrintTable,
  printSheet,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
  Section,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import {
  addManualCharge,
  billingKeys,
  billQuery,
  cancelCharge,
  currentShiftQuery,
  issueBill,
  markBillPrinted,
  priceAgain,
} from '../api/billing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, screen, ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './billing.module.css';
import { BillInsuranceSummary } from '../insurance/bill-insurance.tsx';
import { BillShaSummary } from '../sha/bill-sha.tsx';
import { billSentence, cents, LINE_STATE, METHOD_LABELS, money, moneyColumn } from './format.ts';
import { ReceiptPrinter } from './receipt.tsx';
import { ServicePicker } from './service-picker.tsx';
import { TakePaymentDialog } from './take-payment.tsx';
import { WaiverDialog } from './waiver-dialog.tsx';
import {
  CreditNoteDialog,
  PayRefundDialog,
  REFUND_METHOD_LABELS,
  RefundDialog,
} from './credit-refund.tsx';

/** A bill masked because the visit carries a sensitivity label the user isn't granted. */
function restricted(bill: BillView): boolean {
  return bill.visit.restricted === true;
}

const REQUEST_WORDS: Record<string, string> = {
  requested: 'waiting for approval',
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
  paid: 'paid out',
};

/**
 * The visit bill (plan §6.1): the patient banner, one sentence saying what is owed, every
 * charge with who pays what, and the actions the person's permissions allow. It refreshes
 * every few seconds while something is owed, so a payment taken at another desk shows.
 */
export function BillPanel({ visitId }: { visitId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const bill = useQuery({
    ...billQuery(visitId),
    refetchInterval: (query) =>
      query.state.data !== undefined && cents(query.state.data.totals.owed) > 0n ? 5_000 : false,
  });
  if (session === null) return null;
  return (
    <div className={styles.page}>
      {bill.data === undefined ? null : bill.data.visit.restricted === true ? (
        // A sensitivity label this user's roles aren't granted: the bill stays payable, with
        // the patient by number only and each item as "Restricted item".
        <Notice tone="info">
          {`Restricted record, patient number ${bill.data.visit.patientNumber ?? 'not known'}. Take payment as usual; items are not shown.`}
        </Notice>
      ) : bill.data.visit.patientId === null ? (
        <Notice tone="info">Counter sale: no patient was identified.</Notice>
      ) : (
        <PatientBannerLoader patientId={bill.data.visit.patientId} linked />
      )}
      {bill.isPending ? <LoadingBlock label="Loading the bill" rows={4} /> : null}
      {bill.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void bill.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(bill.error, "The bill couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {bill.data === undefined ? null : (
        <BillBody
          bill={bill.data}
          facilityName={session.facility.name}
          canTake={can(session, 'payments.take')}
          canCharge={can(session, 'billing.charge.manual') && can(session, 'catalogue.read')}
          // Exceptions on a masked bill need someone granted its labels (billing/visibility.ts
          // in the API), so they aren't offered here.
          canCancel={!restricted(bill.data) && can(session, 'billing.charge.cancel')}
          canWaive={!restricted(bill.data) && can(session, 'billing.waiver.request')}
          canCredit={!restricted(bill.data) && can(session, 'billing.credit_note.request')}
          canRefund={!restricted(bill.data) && can(session, 'payments.refund.request')}
          canPayOutRefund={!restricted(bill.data) && can(session, 'payments.take')}
        />
      )}
      {bill.data?.payer.kind === 'sha' ? <BillShaSummary visitId={visitId} /> : null}
      {bill.data?.payer.kind === 'insurer' ? <BillInsuranceSummary visitId={visitId} /> : null}
    </div>
  );
}

function BillBody({
  bill,
  facilityName,
  canTake,
  canCharge,
  canCancel,
  canWaive,
  canCredit,
  canRefund,
  canPayOutRefund,
}: {
  bill: BillView;
  facilityName: string;
  canTake: boolean;
  canCharge: boolean;
  canCancel: boolean;
  canWaive: boolean;
  canCredit: boolean;
  canRefund: boolean;
  canPayOutRefund: boolean;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const shift = useQuery({ ...currentShiftQuery, enabled: canTake });
  const [paying, setPaying] = useState(false);
  const [adding, setAdding] = useState(false);
  const [cancelling, setCancelling] = useState<ChargeView | null>(null);
  const [waiving, setWaiving] = useState<ChargeView | null>(null);
  const [crediting, setCrediting] = useState<ChargeView | null>(null);
  const [refunding, setRefunding] = useState<BillView['payments'][number] | null>(null);
  const [payingOut, setPayingOut] = useState<BillView['refunds'][number] | null>(null);
  const [receiptIds, setReceiptIds] = useState<string[]>([]);
  const [reprint, setReprint] = useState<string | null>(null);
  const sheet = useRef<HTMLElement>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: billingKeys.all });
  const again = useMutation({
    mutationFn: () => priceAgain(bill.visit.id),
    onSuccess: async (result) => {
      await refresh();
      toasts.show({
        title:
          result.stillUnpriced === 0
            ? 'Charges priced'
            : `${String(result.stillUnpriced)} still without a price`,
      });
    },
  });
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelCharge(id, reason),
    onSuccess: async () => {
      await refresh();
      setCancelling(null);
      toasts.show({ title: 'Charge cancelled' });
    },
  });
  const issue = useMutation({
    mutationFn: () => issueBill(bill.visit.id),
    onSuccess: async (result) => {
      await refresh();
      toasts.show({
        title: result.invoice === null ? 'Nothing new to bill' : 'Bill issued',
        ...(result.invoice === null ? {} : { description: result.invoice.number }),
      });
    },
  });
  const printBill = () => {
    if (sheet.current === null) return;
    printSheet(sheet.current);
    void markBillPrinted(bill.visit.id);
  };

  const openShift = shift.data?.shift ?? null;
  const owed = cents(bill.totals.owed) > 0n;
  const hasPending = bill.charges.some((c) => c.status === 'pending' && c.state !== 'not_priced');
  const canPayNow = canTake && owed && openShift !== null && online;
  // P takes payment, the cashier's most frequent action (DESIGN.md §1.5).
  useShortcut(
    'p',
    outsideDialogs(() => {
      setPaying(true);
    }),
    { enabled: canPayNow && !paying, description: 'Take payment' },
  );

  return (
    <>
      <section className={`${styles.section} ${screen.panel}`} aria-labelledby="bill-heading">
        <h2 id="bill-heading" className={styles.sectionHeading}>
          <span>Bill for visit </span>
          <span className="wodi-id">{bill.visit.visitNumber}</span>
        </h2>
        <p className={styles.owed}>{billSentence(bill)}</p>
        <div className={styles.toolbar}>
          {canTake ? (
            <Button
              variant="primary"
              isDisabled={!canPayNow}
              onPress={() => {
                setPaying(true);
              }}
              shortcut="p"
            >
              Take payment
            </Button>
          ) : null}
          {canCharge && bill.visit.status === 'open' ? (
            <Button
              isDisabled={!online}
              onPress={() => {
                setAdding(true);
              }}
            >
              Add charge
            </Button>
          ) : null}
          {canTake && bill.totals.unpricedCount > 0 ? (
            <Button
              isPending={again.isPending}
              pendingLabel="Pricing"
              isDisabled={!online}
              onPress={() => {
                again.mutate();
              }}
            >
              Price again
            </Button>
          ) : null}
          {canTake && hasPending ? (
            <Button
              isPending={issue.isPending}
              pendingLabel="Issuing"
              isDisabled={!online}
              onPress={() => {
                issue.mutate();
              }}
            >
              Issue bill
            </Button>
          ) : null}
          <Button variant="quiet" onPress={printBill}>
            Print bill
          </Button>
          {canPayNow ? <ShortcutHint combo="p" action="take payment" /> : null}
        </div>
        {canTake && openShift === null && shift.isSuccess ? (
          <Notice tone="info">Open a cashier shift before taking payment.</Notice>
        ) : null}
        {again.isError ? (
          <Notice tone="error">{errorText(again.error, 'Try again.')}</Notice>
        ) : null}
        {issue.isError ? (
          <Notice tone="error">{errorText(issue.error, 'Try again.')}</Notice>
        ) : null}
        <TableScroll>
          <ChargesTable
            bill={bill}
            {...(canCancel
              ? {
                  onCancel: (c: ChargeView) => {
                    setCancelling(c);
                  },
                }
              : {})}
            {...(canWaive
              ? {
                  onWaive: (c: ChargeView) => {
                    setWaiving(c);
                  },
                }
              : {})}
            {...(canCredit
              ? {
                  onCredit: (c: ChargeView) => {
                    setCrediting(c);
                  },
                }
              : {})}
          />
        </TableScroll>
      </section>

      <Section title="Payments">
        <TableScroll>
          <Table flat aria-label="Payments" dense>
            <TableHeader>
              <Column isRowHeader>Receipt no.</Column>
              <Column>Received</Column>
              <Column>Method</Column>
              <Column>Reference</Column>
              <Column numeric>Amount (KES)</Column>
              <Column>Cashier</Column>
              <Column>Receipt</Column>
            </TableHeader>
            <TableBody items={bill.payments} renderEmptyState={() => 'No payments yet.'}>
              {(p) => (
                <Row id={p.id}>
                  <Cell>
                    <span className="wodi-id">{p.receipt?.number ?? ''}</span>
                  </Cell>
                  <Cell>{formatInstant(p.receivedAt)}</Cell>
                  <Cell>{METHOD_LABELS[p.method]}</Cell>
                  <Cell>
                    <span className="wodi-id">{p.reference ?? ''}</span>
                  </Cell>
                  <Cell numeric>{moneyColumn(p.amount)}</Cell>
                  <Cell>{p.cashier.name}</Cell>
                  <Cell>
                    {p.receipt === null ? null : (
                      <Button
                        dense
                        variant="quiet"
                        onPress={() => {
                          setReprint(p.receipt?.id ?? null);
                        }}
                      >
                        Show receipt
                      </Button>
                    )}
                    {canRefund && cents(p.refundable) > 0n ? (
                      <Button
                        dense
                        variant="quiet"
                        onPress={() => {
                          setRefunding(p);
                        }}
                      >
                        Ask for a refund
                      </Button>
                    ) : null}
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>

      {bill.creditNotes.length + bill.refunds.length > 0 ? (
        <Section title="Credit notes and refunds">
          <ul className={styles.section}>
            {bill.creditNotes.map((n) => (
              <li key={n.id}>
                {n.number === null ? null : <span className="wodi-id">{`${n.number} `}</span>}
                {`Credit note of ${money(n.total)} on `}
                <span className="wodi-id">{n.invoiceNumber}</span>
                {`: ${REQUEST_WORDS[n.status]}, asked by ${n.requestedBy.name}.`}
              </li>
            ))}
            {bill.refunds.map((r) => (
              <li key={r.id} className={styles.toolbar}>
                <span>
                  <span className="wodi-id">{`${r.number} `}</span>
                  {`Refund of ${money(r.amount)} by ${REFUND_METHOD_LABELS[r.method].toLowerCase()}: ${REQUEST_WORDS[r.status]}.`}
                  {r.externalReference === null ? null : (
                    <span className="wodi-id">{` ${r.externalReference}`}</span>
                  )}
                </span>
                {canPayOutRefund && r.status === 'approved' ? (
                  <Button
                    dense
                    isDisabled={!online || (r.method === 'cash' && openShift === null)}
                    onPress={() => {
                      setPayingOut(r);
                    }}
                  >
                    Pay out refund
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {crediting === null ? null : (
        <CreditNoteDialog
          charge={crediting}
          onClose={() => {
            setCrediting(null);
          }}
        />
      )}
      {refunding === null ? null : (
        <RefundDialog
          payment={refunding}
          onClose={() => {
            setRefunding(null);
          }}
        />
      )}
      {payingOut === null ? null : (
        <PayRefundDialog
          refund={payingOut}
          onClose={() => {
            setPayingOut(null);
          }}
        />
      )}

      {receiptIds.map((id) => (
        <div key={id} className={styles.receiptPreview}>
          <ReceiptPrinter receiptId={id} auto />
        </div>
      ))}
      {reprint === null ? null : (
        <Dialog
          title="Receipt"
          isOpen
          onOpenChange={(open) => {
            if (!open) setReprint(null);
          }}
        >
          <ReceiptPrinter receiptId={reprint} auto={false} />
          <DialogActions>
            <Button
              onPress={() => {
                setReprint(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {paying ? (
        <TakePaymentDialog
          bill={bill}
          isOpen={paying}
          onOpenChange={setPaying}
          onPaid={setReceiptIds}
        />
      ) : null}
      {waiving === null ? null : (
        <WaiverDialog
          charge={waiving}
          onClose={() => {
            setWaiving(null);
          }}
        />
      )}
      {adding ? (
        <AddChargeDialog
          visitId={bill.visit.id}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
      <ReasonDialog
        title="Cancel this charge?"
        isOpen={cancelling !== null}
        onOpenChange={(open) => {
          if (!open) setCancelling(null);
        }}
        confirmLabel="Cancel charge"
        pendingLabel="Cancelling"
        isPending={cancel.isPending}
        error={cancel.isError ? errorText(cancel.error, 'Try again.') : null}
        onConfirm={(reason) => {
          if (cancelling !== null) cancel.mutate({ id: cancelling.id, reason });
        }}
      >
        <p>
          {cancelling === null
            ? ''
            : `${cancelling.itemName}, ${money(cancelling.total)}. The charge stays on the bill as cancelled, with your reason.`}
        </p>
      </ReasonDialog>

      <div className={styles.printOnly}>
        <BillSheet bill={bill} facilityName={facilityName} sheetRef={sheet} />
      </div>
    </>
  );
}

function ChargesTable({
  bill,
  onCancel,
  onWaive,
  onCredit,
}: {
  bill: BillView;
  onCancel?: (c: ChargeView) => void;
  onWaive?: (c: ChargeView) => void;
  onCredit?: (c: ChargeView) => void;
}) {
  const openRequest = new Map(
    bill.adjustments.filter((a) => a.status === 'requested').map((a) => [a.chargeId, a]),
  );
  const hasActions = onCancel !== undefined || onWaive !== undefined || onCredit !== undefined;
  return (
    <Table flat aria-label="Charges">
      <TableHeader>
        <Column isRowHeader>Item</Column>
        <Column numeric>Qty</Column>
        <Column numeric>Price (KES)</Column>
        {bill.payer.kind === 'cash' ? null : <Column numeric>Payer pays</Column>}
        <Column numeric>Patient pays</Column>
        <Column numeric>Paid</Column>
        <Column numeric>Owes</Column>
        <Column>State</Column>
        {hasActions ? <Column>Action</Column> : null}
      </TableHeader>
      <TableBody items={bill.charges} renderEmptyState={() => 'No charges on this visit yet.'}>
        {(c) => (
          <Row id={c.id}>
            <Cell>
              {c.itemName}
              {c.state === 'not_priced' && c.unpricedMessage !== null ? (
                <div className={styles.meta}>{c.unpricedMessage}</div>
              ) : null}
              {c.manualReason === null ? null : (
                <div className={styles.meta}>{`Added by hand: ${c.manualReason}`}</div>
              )}
              {c.cancelReason === null ? null : (
                <div className={styles.meta}>{`Cancelled: ${c.cancelReason}`}</div>
              )}
              {openRequest.has(c.id) ? (
                <div className={styles.meta}>
                  {`${openRequest.get(c.id)?.kind === 'discount' ? 'Discount' : 'Waiver'} of ${money(openRequest.get(c.id)?.amount ?? c.due)} waiting for approval, asked by ${openRequest.get(c.id)?.requestedBy.name ?? ''}`}
                </div>
              ) : null}
            </Cell>
            <Cell numeric>{c.quantity}</Cell>
            <Cell numeric>{c.unitPrice === null ? 'Not priced' : moneyColumn(c.unitPrice)}</Cell>
            {bill.payer.kind === 'cash' ? null : <Cell numeric>{moneyColumn(c.payerAmount)}</Cell>}
            <Cell numeric>{moneyColumn(c.due)}</Cell>
            <Cell numeric>{moneyColumn(c.paid)}</Cell>
            <Cell numeric>{moneyColumn(c.owed)}</Cell>
            <Cell>
              <Tag state={LINE_STATE[c.state].tag}>{LINE_STATE[c.state].text}</Tag>
            </Cell>
            {hasActions ? (
              <Cell>
                <span className={styles.actionCell}>
                  {onWaive !== undefined &&
                  c.status === 'pending' &&
                  c.state !== 'not_priced' &&
                  cents(c.due) > 0n &&
                  !openRequest.has(c.id) ? (
                    <Button
                      dense
                      variant="quiet"
                      onPress={() => {
                        onWaive(c);
                      }}
                    >
                      Ask for a waiver
                    </Button>
                  ) : null}
                  {onCredit !== undefined &&
                  c.status === 'billed' &&
                  c.invoiceLineId !== null &&
                  cents(c.creditable) > 0n ? (
                    <Button
                      dense
                      variant="quiet"
                      onPress={() => {
                        onCredit(c);
                      }}
                    >
                      Ask for a credit note
                    </Button>
                  ) : null}
                  {onCancel !== undefined && c.status === 'pending' ? (
                    <Button
                      dense
                      variant="quiet"
                      onPress={() => {
                        onCancel(c);
                      }}
                    >
                      Cancel charge
                    </Button>
                  ) : null}
                </span>
              </Cell>
            ) : null}
          </Row>
        )}
      </TableBody>
    </Table>
  );
}

/** Manual charge: catalogue price only, a reason always (domain rule 2; decision 17). */
function AddChargeDialog({ visitId, onClose }: { visitId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [item, setItem] = useState<ServiceItemView | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(() => crypto.randomUUID());
  const qty = /^\d{1,5}$/.test(quantity.trim()) ? Number(quantity.trim()) : 0;
  const valid = item !== null && qty >= 1 && reason.trim().length >= 3;
  const add = useMutation({
    mutationFn: () =>
      addManualCharge(visitId, {
        clientRequestId: requestKey,
        serviceItemId: item?.id ?? '',
        quantity: qty,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({ title: 'Charge added' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Add charge"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          Charges normally come from what was done. Add one by hand only for something that wasn't
          recorded, and say why. It is charged at the catalogue price.
        </p>
        {item === null ? (
          <ServicePicker label="Service or item" onSelect={setItem} />
        ) : (
          <p className={styles.toolbar}>
            <span className="wodi-id">{item.code}</span>
            <span>{item.name}</span>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                setItem(null);
              }}
            >
              Change
            </Button>
          </p>
        )}
        {tried && item === null ? <Notice tone="info">Choose a service or item.</Notice> : null}
        <TextField
          label="Quantity"
          inputMode="numeric"
          value={quantity}
          onChange={setQuantity}
          {...(tried && qty < 1 ? { errorMessage: 'Enter a whole number of at least 1.' } : {})}
        />
        <TextArea
          label="Reason"
          value={reason}
          onChange={setReason}
          {...(tried && reason.trim().length < 3
            ? { errorMessage: 'Say why this is added by hand.' }
            : {})}
        />
        {add.isError ? (
          <Notice tone="error">
            {errorText(add.error, "The charge wasn't added. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={add.isPending}
          pendingLabel="Adding"
          onPress={() => {
            setTried(true);
            if (valid) add.mutate();
          }}
        >
          Add charge
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The itemised bill on A4 (plan §6.5). Printing it is audited. */
function BillSheet({
  bill,
  facilityName,
  sheetRef,
}: {
  bill: BillView;
  facilityName: string;
  sheetRef: React.Ref<HTMLElement>;
}) {
  const live = bill.charges.filter((c) => c.status !== 'cancelled');
  return (
    <PrintSheet paper="a4" label="Bill" ref={sheetRef}>
      <PrintHeader facility={facilityName} title="Bill" />
      <PrintLines
        items={[
          { label: 'Visit no.', value: bill.visit.visitNumber, identifier: true },
          { label: 'Date', value: formatInstant(bill.visit.startedAt) },
          { label: 'Payer', value: bill.payer.name },
        ]}
      />
      <PrintTable>
        <thead>
          <tr>
            <th>Item</th>
            <th data-numeric="">Qty</th>
            <th data-numeric="">Amount</th>
            <th data-numeric="">Patient pays</th>
          </tr>
        </thead>
        <tbody>
          {live.map((c) => (
            <tr key={c.id}>
              <td>{c.itemName}</td>
              <td data-numeric="">{c.quantity}</td>
              <td data-numeric="">{c.state === 'not_priced' ? 'Not priced' : money(c.total)}</td>
              <td data-numeric="">{money(c.due)}</td>
            </tr>
          ))}
        </tbody>
      </PrintTable>
      <PrintLines
        items={[
          { label: 'Paid', value: money(bill.totals.paid) },
          { label: 'Owed', value: money(bill.totals.owed) },
        ]}
      />
    </PrintSheet>
  );
}
